import { useState, useEffect, useContext, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link} from 'react-router-dom';
import { faArrowLeftLong, faChevronDown, faChevronRight, faDice, faDownload, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import sengiCoin from '../../assets/sengi_coin_white.png';
import demoImage1 from '../../assets/cyperpunktDemoImage.png';
import demoImage2 from '../../assets/demoPortrai.jpg';
import demoImage3 from '../../assets/demo3.jpg';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { stateIconDone, stateIconError, stateIconWaiting } from '../../types/ai/states';
import { ImageGernerationSimpleInputProps, ImageGernerationSimpleProps } from '../../types/imageSimple/imageGeneration';
import EffectThemeDropdown from '../../components/effectTheme/effectThemeDropdown';
import { renderStyleDropdownItem, styleFilterGroups, stylesList } from '../../utils/images/imageStylesList';
import { aspectRatioList, getImagePixelSize, valueIsAspectRatioType } from '../../utils/images/imageConvertFormats';
import EffectThemeRange from '../../components/effectTheme/effectThemeRange';
import { CfgInfoElement, QualityInfoElement, SamplerInfoElement, SamplingStepsInfoElement, SchedulerInfoElement, SeedInfoElement } from '../../utils/aiModels/infotexts';
import { InfoPopoverButton } from '../../components/app/ImageStudio/InfoButton';
import { ImageCreateProps, ImageGenerationBodyProps, qualityList, samplerList, schedulerList } from '../../types/ai/api';
import EffectThemeInput from '../../components/effectTheme/effectThemeInput';
import { Form } from 'react-bootstrap';
import { handleGetRandomNumber } from '../../utils/calculations';
import { ApiPayment } from '../../apis/ApiPayment';
import { styleDescriptionPrompt } from '../../utils/aiModels/prompts';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedImage } from '../../components/ui/AuthedImage';



const exampleData: ImageGernerationSimpleProps[] = [
    {
        id:1,
        src:[demoImage3],
        promptPositive:'Peaceful sunset over a calm lake, reflection in the water, warm golden hour colors, soft light rays, serene atmosphere, photorealistic, high detail',
        promptNegative: 'no people, no boats, no fog, no oversaturated colors, no artificial colors, no blurriness, no noise'
    },
    {
        id:2,
        src:[demoImage2],
        promptPositive:'Portrait of an elegant woman in the style of Alfons Mucha, floral ornaments, flowing hair, pastel tones, soft lighting, detailed line work, artistic',
        promptNegative: 'no modern elements, no photography, no harsh shadows, no dark or gloomy mood, no digital artifacts'
    },
    {
        id:3,
        src:[demoImage1],
        promptPositive:'Cyberpunk city at night, neon lights, wet streets with reflections, futuristic architecture, dynamic perspective, atmospheric lighting',
        promptNegative:'no crowds, no smog, no blur, no explosions, no old-fashioned cars, no dull gray colors'
    },
    {
        id:4,
        src:[sengiCoin],
        promptPositive:'Highly detailed coin design featuring a sengi (elephant shrew), engraved style, intricate patterns, realistic metallic texture, elegant ornamental borders, shiny gold or silver surface',
        promptNegative: 'no other animals, no background scenery, no cartoonish style, no color noise, no scratches, no unrealistic textures'
    },
    {
        id:5,
        src:[demoImage1,demoImage2,demoImage3,demoImage1],
        promptPositive:'Peaceful sunset over a calm lake, reflection in the water, warm golden hour colors, soft light rays, serene atmosphere, photorealistic, high detail',
        promptNegative: 'no people, no boats, no fog, no oversaturated colors, no artificial colors, no blurriness, no noise'
    }
];

export const ImageGeneration = () => {
    const { t } = useTranslation('generation');
    const [generatedImages,setGeneratedImages] = useState<ImageGernerationSimpleProps[]>([]);
    const [generation,state,setState,getJobID,insufficientCreditsDialog] = useAiImageTypesGeneration();
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const [seedRandomActive, setSeedRandomActive] = useState(true);
    const [creationCosts, setCreationCosts] = useState<number|null>(0);
    const [inputs, setInputs] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)'
    });

    const handleGenerationResult = () => {
        let img = generation;
        //add image to history list
        const newGeneratedImages:ImageGernerationSimpleProps[] = [...generatedImages];
        let lastID = newGeneratedImages.length > 0 ? newGeneratedImages[newGeneratedImages.length-1].id : 0;
        newGeneratedImages.push({
            'id':lastID+1,
            'promptPositive': inputs.promptPositive,
            'promptNegative': inputs.promptNegative,
            'src': img
        });
        setGeneratedImages(newGeneratedImages);
    };
    const generateImage = async() => {
        setState(null);
        //request to get job id
        const imageDimension = getImagePixelSize(valueIsAspectRatioType(inputs.aspectRatio) ?  inputs.aspectRatio: '1:1 (quadratic)', inputs.quality ?? 'standard');
        const prompt = styleDescriptionPrompt(inputs.promptPositive,inputs.promptNegative, inputs.style, false);
        const body: ImageGenerationBodyProps = {
            positive_prompt: prompt.positive_prompt, 
            negative_prompt: prompt.negative_prompt ? prompt.negative_prompt : undefined,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputs.seed,
            steps: inputs.samplingSteps,
            scheduler: inputs.scheduler,
            model: 'Qwen_Image',
            cfg: inputs.cfg,
            number_of_images: 1,
            sampler: inputs.sampler
        };
        setState('send');
        let path = 'generate/image'
        try {
            getJobID('image_gen',path,body)
        } catch(err){
            setState('error');
        };
        
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        setState(null);
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const downloadImages = (imageID:number) => {
        const imgObject: ImageGernerationSimpleProps | undefined = generatedImages.find(item => item.id === imageID);
        if(imgObject === undefined){return;}
        for(let imgUrl of imgObject.src){
            const fileName = imgUrl.replaceAll('\\','/').split('/').slice(-1)[0];
            downloadOriginal(imgUrl, fileName).catch(err => console.error('Download fehlgeschlagen', err));
        }
    };
    const updateCosts = async() => {
        const imageDimension = getImagePixelSize(valueIsAspectRatioType(inputs.aspectRatio) ?  inputs.aspectRatio: '1:1 (quadratic)', inputs.quality);
        try{
            let dataObject: ImageCreateProps = {
                imgWidth: imageDimension.width,
                imgHeight: imageDimension.height,
                numberOfGenerations: 1,
                samplingSteps: inputs.samplingSteps,
                cfg: inputs.cfg,
                sampler: inputs.sampler,
                scheduler: inputs.scheduler,
                model:'Qwen',
                reference:  null
            }
            await ApiPayment.GetCostsForImageCreate(dataObject)
            .then((response)=>{
                console.log(response);
                setCreationCosts(response.costs);
            })
            .catch(()=>{
                console.log('Error in calculation creation costs.');
            });
        }
        catch{
            console.log('Error in calculation creation costs.');
        }
    };

    useEffect(()=>{
        if(generation.length > 0){
            handleGenerationResult();
        }
    },[generation]);
    useEffect(()=>{
        updateCosts();
    },[inputs]);
  return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <Link 
                            to="/executions" 
                            className="btn btn-outline-secondary btn-sm ms-3"
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </Link>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                {state
                    ? state === 'send'
                        ? <p className='h4'>{t('state.sending')}</p>
                        : state === 'waiting'
                            ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                            : state === 'error'
                                ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                : state === 'done'
                                    ? <p className='h4'>{stateIconDone} {t('state.done')}</p>
                                    : <></>
                    : <></>
                }
                {generatedImages.slice().sort((a, b) => b.id - a.id).map(item=>(
                    <div className='row py-2 text-center align-items-center border-bottom border-secondary'>
                        <div className='col-5 d-flex flex-wrap py-auto' style={{gap:'10px'}}>
                            {item.src.map(img => (
                                <AuthedImage
                                    src={img}
                                    alt={`Image ${item.id}`}
                                    className={'rounded d-block'}
                                    style={{height:'200px', width: 'auto'}}
                                />
                            ))
                            }
                        </div>
                        <div className='col d-flex flex-column justify-content-center text-start'>
                            <div className='row mb-2'>
                                <p className='fw-bold'>{t('prompt.positiveShort')}</p>
                                <p className='mb-2'>{item.promptPositive}</p>
                                <p className='fw-bold'>{t('prompt.negativeShort')}</p>
                                <p className='mb-2'>{item.promptNegative}</p>
                            </div>
                            <div className='row'>
                                <div className='col-auto p-0'>
                                    <button
                                        className="btn btn-outline-secondary"
                                        title={t('download.image')}
                                        onClick={() => downloadImages(item.id)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faDownload}
                                            size='xl'
                                            className='bg-transparent me-2'
                                        />
                                        {t('download.image')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                ))}
                </div>
                
                <div className='row p-3 pb-1'>
                    <div className='col'>
                        <label htmlFor="promptPositive" className="form-label">{t('prompt.positive')}</label>
                        <textarea
                            className="form-control"
                            id="promptPositive"
                            name='promptPositive'
                            rows={3}
                            value={inputs.promptPositive}
                            onChange={(e) => handleInputChange(e)}
                            placeholder={t('placeholder.positive')}
                        />
                    </div>
                    <div className='col'>
                        <label htmlFor="promptNegative" className="form-label">{t('prompt.negative')}</label>
                        <textarea
                            className="form-control"
                            id="promptNegative"
                            name='promptNegative'
                            rows={3}
                            value={inputs.promptNegative}
                            onChange={(e) => handleInputChange(e)}
                            placeholder={t('placeholder.negative')}
                        />
                    </div>
                    <div className='col-auto d-flex flex-column justify-content-center text-start'>
                        <button
                            className="btn btn-secondary"
                            title={t('generateImage')}
                            onClick={() => generateImage()}
                        >
                            <FontAwesomeIcon
                            icon={faWandMagicSparkles}
                            className="me-2"
                        />
                         {t('coins')} {creationCosts ?? '—'}
                    </button>
                    </div>
                </div>
                
                <div className='row mb-2'>
                    <button
                        className="btn btn-transparent m-0 p-0 btn-lg text-start"
                        onClick={() => setShowSettings(!showSettings)}
                    >
                        <FontAwesomeIcon
                            icon={showSettings ? faChevronDown : faChevronRight}
                            className="me-2"
                        />
                        {t('promptSettings')}
                    </button>
                </div>
                {
                    showSettings
                    ?   <>
                            <div className='row m-0 ps-3 ms-1'>
                                <p className='test-small text-secondary m-0'>{t('basic')}</p>
                            </div>
                            <div className='row ps-3 m-0 ms-1'>
                                <div className='col-2'>
                                    <div className="mb-1">{t('aspectRatio')}</div>
                                    <EffectThemeDropdown
                                        className="w-100 mb-2"
                                    >
                                        <EffectThemeDropdown.Toggle
                                            id="dropdown-ai-model"
                                            className="w-100"
                                        >
                                            {inputs.aspectRatio}
                                        </EffectThemeDropdown.Toggle>

                                        <EffectThemeDropdown.Menu 
                                            className="p-1 w-100"
                                            maxHeight={420}
                                            style={{ zIndex: 999999 }}
                                        >
                                            {aspectRatioList.map((item, index) => (
                                            <EffectThemeDropdown.Item
                                                key={index}
                                                onClick={()=> setInputs(prev => ({...prev,'aspectRatio': item}))}
                                                active={item === inputs.aspectRatio}
                                            >
                                                {item}
                                            </EffectThemeDropdown.Item>
                                            ))}
                                        </EffectThemeDropdown.Menu>
                                    </EffectThemeDropdown>
                                </div>
                                <div className='col-3'>
                                    <div className="mb-1">{t('style')}</div>
                                    <EffectThemeDropdown.SearchFilter
                                        id="dropdown-style-model"
                                        className="w-100 mb-2"
                                        selectedLabel={stylesList.filter(x => x.item === inputs.style).length > 0 ? stylesList.filter(x => x.item === inputs.style)[0].name : ''}
                                        items={stylesList}
                                        selectedItem={stylesList.find(x => x.item === inputs.style) ?? null}
                                        onSelect={(x) => {
                                            setInputs(prev => ({...prev, "style": x.item}))
                                        }}
                                        getItemKey={(x) => x.item}
                                        getItemSearchText={(x) =>`${x.name ?? ""} ${x.item} ${(x.category ?? []).join(" ")}`}
                                        renderItem={renderStyleDropdownItem}
                                        filterGroups={styleFilterGroups}
                                        searchPlaceholder={t('placeholder.searchStyle')}
                                        resetKey={"create-image"}
                                        maxHeight={420}
                                    />
                                </div>
                                
                            </div>
                            <div className='row m-0 ps-3 ms-1'>
                                <p className='test-small text-secondary m-0'>{t('advanced')}</p>
                            </div>
                            <div className='row ps-3 m-0 ms-1'>
                                <div className='col-2'>
                                    <div className="row m-0 p-0">
                                        <div className="col-auto mb-1 ps-0"> {t('samplingSteps')}</div>
                                        <div className="col">{inputs.samplingSteps}</div>
                                        <div className="col-auto mb-1 p-0">
                                            <InfoPopoverButton
                                                width={320}
                                                placement="right"
                                            >
                                                <SamplingStepsInfoElement/>
                                            </InfoPopoverButton>
                                        </div>
                                    </div>
                                    <EffectThemeRange
                                        min={1}
                                        max={40}
                                        step={1}
                                        name="samplingSteps"
                                        value={inputs.samplingSteps}
                                        onChange={val => setInputs(prev => ({...prev,'samplingSteps': val}))}
                                        minDescription={t('range.fast')}
                                        maxDescription={t('range.slow')}
                                        className="mb-2"
                                    />
                                </div>
                                <div className='col-2'>
                                    <div className="row m-0 p-0 align-items-center">
                                        <div className="col-auto mb-1 ps-0">
                                            {t('cfgScaleGuidance')}
                                        </div>
                                        <div className="col text-end">
                                            {inputs.cfg}
                                        </div>
                                        <div className="col-auto mb-1 p-0">
                                            <InfoPopoverButton
                                                width={320}
                                                placement="right"
                                            >
                                                <CfgInfoElement/>
                                            </InfoPopoverButton>
                                        </div>
                                    </div>
                                    <EffectThemeRange
                                        min={0}
                                        max={20}
                                        step={0.1}
                                        name="cfg"
                                        value={inputs.cfg}
                                        onChange={val => setInputs(prev => ({...prev,'cfg': val}))}
                                        minDescription={t('range.creative')}
                                        maxDescription={t('range.exact')}
                                        className="mb-2"
                                    />
                                </div>
                                <div className='col-2'>
                                    <div className="row m-0 p-0">
                                        <div className="col-auto mb-1 ps-0">{t('sampler')}</div>
                                        <div className="col"></div>
                                        <div className="col-auto mb-1 p-0">
                                            <InfoPopoverButton
                                                width={320}
                                                placement="right"
                                            >
                                                <SamplerInfoElement/>
                                            </InfoPopoverButton>
                                        </div>
                                    </div>
                                    <EffectThemeDropdown className="w-100 mb-2">
                                        <EffectThemeDropdown.Toggle
                                            id="dropdown-sampler"
                                            className="w-100"
                                        >
                                            {inputs.sampler}
                                        </EffectThemeDropdown.Toggle>

                                        <EffectThemeDropdown.Menu className="p-1 w-100">
                                            {samplerList.map((item, index) => (
                                            <EffectThemeDropdown.Item
                                                key={index}
                                                onClick={()=> setInputs(prev => ({...prev, 'sampler': item}))}
                                                active={item === inputs.sampler}
                                            >
                                                {item}
                                            </EffectThemeDropdown.Item>
                                            ))}
                                        </EffectThemeDropdown.Menu>
                                    </EffectThemeDropdown>
                                </div>
                                <div className='col-2'>
                                    <div className="row m-0 p-0">
                                        <div className="col-auto mb-1 ps-0">{t('scheduler')}</div>
                                        <div className="col"></div>
                                        <div className="col-auto mb-1 p-0">
                                            <InfoPopoverButton
                                                width={320}
                                                placement="right"
                                            >
                                                <SchedulerInfoElement/>
                                            </InfoPopoverButton>
                                        </div>
                                    </div>
                                    <EffectThemeDropdown className="w-100 mb-2">
                                        <EffectThemeDropdown.Toggle
                                            id="dropdown-scheduler"
                                            className="w-100"
                                        >
                                            {inputs.scheduler}
                                        </EffectThemeDropdown.Toggle>

                                        <EffectThemeDropdown.Menu className="p-1 w-100">
                                            {schedulerList.map((item, index) => (
                                            <EffectThemeDropdown.Item
                                                key={index}
                                                onClick={()=> setInputs(prev => ({...prev,'scheduler': item}))}
                                                active={item === inputs.scheduler}
                                            >
                                                {item}
                                            </EffectThemeDropdown.Item>
                                            ))}
                                        </EffectThemeDropdown.Menu>
                                    </EffectThemeDropdown>
                                </div>
                                <div className='col-2'>
                                    <div className="row m-0 p-0">
                                        <div className="col-auto mb-1 ps-0">{t('quality')}</div>
                                        <div className="col"></div>
                                        <div className="col-auto mb-1 p-0">
                                            <InfoPopoverButton
                                                width={320}
                                                placement="right"
                                            >
                                                <QualityInfoElement/>
                                            </InfoPopoverButton>
                                        </div>
                                    </div>
                                    <EffectThemeDropdown className="w-100 mb-2">
                                        <EffectThemeDropdown.Toggle
                                            id="dropdown-quality"
                                            className="w-100"
                                        >
                                            {inputs.quality}
                                        </EffectThemeDropdown.Toggle>

                                        <EffectThemeDropdown.Menu className="p-1 w-100">
                                            {qualityList.map((item, index) => (
                                            <EffectThemeDropdown.Item
                                                key={index}
                                                onClick={()=> setInputs(prev => ({...prev,'quality': item}))}
                                                active={item === inputs.quality}
                                            >
                                                {item}
                                            </EffectThemeDropdown.Item>
                                            ))}
                                        </EffectThemeDropdown.Menu>
                                    </EffectThemeDropdown>
                                </div>
                                <div className='col-2'>
                                    <div className="row m-0 p-0">
                                        <div className="col-auto mb-1 ps-0">{t('seed')}</div>
                                        <div className="col"></div>
                                        <div className="col-auto mb-1 p-0">
                                            <InfoPopoverButton
                                                width={320}
                                                placement="right"
                                            >
                                                <SeedInfoElement/>
                                            </InfoPopoverButton>
                                        </div>
                                    </div>
                                    <div className="row m-0 p-0">
                                        <div className="col ps-0">
                                            <EffectThemeInput
                                                type="number"
                                                name="seed"
                                                value={inputs.seed}
                                                min={-1}
                                                max={999999}
                                                step={1}
                                                inputClassName="mb-2"
                                                onValueChange={val => {
                                                    setInputs(prev => ({ ...prev, ['seed']: Number(val) }));
                                                }}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <button
                                                className="btn btn-transparent m-0 p-0"
                                                title={t('getRandomSeed')}
                                                onClick={() => setInputs(p => ({ ...p, seed: handleGetRandomNumber() }))}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faDice}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row m-0 p-0">
                                        <div className="ps-0">
                                            <Form.Check
                                                label={t('changeSeedAuto')}
                                                type="checkbox"
                                                title={t('changeSeedAutoHint')}
                                                checked={seedRandomActive}
                                                onChange={() => {
                                                    setSeedRandomActive(prev => !prev);
                                                }}
                                                style={{ fontSize: 16 }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    :   <></>
                }
            </div>
        </div>
    </>
  );
};

export default ImageGeneration;